/**
 * Shared API response/payload types for the webclient.
 *
 * Field shapes are derived from the bff-api / pic-service / order-service
 * responses observed at consumer sites. Backend remains the source of
 * truth — these types are best-effort reflections, not generated.
 */

import type { ApiAddress } from '../utils/addressUtils';
import type { AlbumAccess } from './albumAccess';
import type { Locale } from '../i18n/locales';
import type { MediaType } from './mediaTypes';
import type { PictureOrientation } from './pictureOrientations';
import type { ThemeTokens } from './shopPresets';

export type Uuid = string;

export interface Setting {
  web_title?: string | null;
  footer_content?: string | null;
  disable_vat_billing?: boolean;
  /** Registration wall for albums; absent reads as 'open' (readAlbumAccess). */
  album_access?: AlbumAccess;
  /** Signed-in customers must confirm their email with a code before ordering; absent reads as false. */
  require_verified_email?: boolean;
  theme?: ThemeTokens | null;
  logo_url?: string | null;
  /** Whitelabel Shop basics: true hides "Powered by Foto2Me" in the footer and
   *  shop mails. Absent for a shop without whitelabel (the credit shows). */
  hide_platform_branding?: boolean;
  favicon_url?: string | null;
  facebook_login?: { enabled: boolean; app_id?: string | null };
  google_login?: { enabled: boolean; client_id?: string | null };
  // Merchant identity fields (payment-compliance): the legal company name and
  // registered seat (as one line) shown in the SimplePay data-transfer declaration.
  // May be null/absent — the declaration modal falls back to web_title/display_name.
  company_name?: string | null;
  registered_seat_line?: string | null;
  // Barion Base Pixel id (payment-compliance follow-ups). May be `""` when
  // unset — gate on truthiness/non-empty, not just non-null.
  barion_pixel_id?: string | null;
  [key: string]: unknown;
}

/** Whether the shop serves its customers (user-service accounts.shop_state):
 *  `open` everything; `orders_paused` no album browsing, cart, checkout or
 *  paying, but orders, downloads, messages and the profile still work;
 *  `closed` only the landing page, static pages and logins of staff. Staff
 *  and impersonated sessions are never held back. */
export type ShopState = 'open' | 'orders_paused' | 'closed';

export const SHOP_STATES: readonly ShopState[] = ['open', 'orders_paused', 'closed'];

export interface Account {
  id: Uuid;
  name: string;
  permissions?: string[];
  currency_code?: string;
  currency_precision?: number;
  created_at?: string;
  setting?: Setting;
  shop_state?: ShopState;
  [key: string]: unknown;
}

export type AccountRole = 'customer' | 'account_sales' | 'account_retoucher' | 'account_admin' | 'global_admin';

export interface User {
  id: Uuid;
  email: string;
  name?: string;
  account_id?: Uuid;
  roles?: AccountRole[];
  account?: Account;
  email_verified?: boolean;
  provider?: string | null;
  guest?: boolean;
  created_at?: string;
  enabled_locales?: Locale[];
  /** The user's chosen UI language (users.locale). Null when never chosen. */
  locale?: Locale | null;
  /** Staff opt-out for the reminder email (users.reminder_emails). Default true. */
  reminder_emails?: boolean;
  [key: string]: unknown;
}

/** `system` rows are produced by services (today: Relations reminders), never by a person. */
export type MessageSender = 'customer' | 'admin' | 'system';
export type MessageStatus = 'sent' | 'seen';
export type SystemMessageKind = 'reminder';

/**
 * The raw values a `kind: 'reminder'` system message carries, so the client
 * can render the instant in the SHOP's zone (`useShopDateFormat`) instead of
 * re-parsing the server's rendered `body`.
 */
export interface ReminderPayload {
  item: 'task' | 'event';
  title: string;
  /** UTC instant; all-day items store shop-local 00:00. */
  anchor_at: string;
  all_day: boolean;
  partner_name?: string | null;
  offset_minutes: ReminderOffsetMinutes;
}

export interface ChatMessage {
  id: Uuid;
  sender: MessageSender;
  body: string;
  status: MessageStatus;
  created_at: string;
  admin_user_id: Uuid | null;
  admin_name: string | null;
  /** Null on the customer chat's own rows. */
  kind: SystemMessageKind | null;
  /** Site-relative /admin path; null when the message has no target. */
  link_path: string | null;
  /** `{}` on non-system rows. */
  payload: ReminderPayload | Record<string, never>;
}

/** Admin inbox triage status (admin inbox spec, revision 2). */
export type ConversationStatus = 'new' | 'follow_up' | 'done';
export type InboxView = ConversationStatus;

/** One admin-inbox row. */
export interface Conversation {
  user_id: Uuid;
  /** Null when the buyer has no name; show the email instead. */
  user_name: string | null;
  user_email: string | null;
  last_message_at: string;
  status: ConversationStatus;
  /** Compare-and-set token for replies and status changes. */
  version: number;
}

export interface ConversationPage {
  conversations: Conversation[];
  /** Opaque; null on the last page. */
  next_cursor: string | null;
}

export interface AdminThread {
  /** Null for a buyer who never wrote. */
  conversation: Conversation | null;
  messages: ChatMessage[];
}

/** GET /admin/messages/count — badge, chips, dashboard card. */
export interface MessageCounts {
  new: number;
  follow_up: number;
}

export interface ReplyResult {
  message: ChatMessage;
  conversation: Conversation;
}

export type { MediaType };

export interface Media {
  id: Uuid;
  media_type?: MediaType;
  /** Orientation bucket derived from pixel dimensions; absent when unknown. */
  orientation?: PictureOrientation | null;
  /** Pixel dimensions (display orientation, shared by the renditions); absent when unknown. */
  width?: number;
  height?: number;
  // null whenever pic-service has no rendition of the live master (processing,
  // failed, or the master was replaced and not re-rendered yet).
  url?: string | null; // preview (watermarked photo / video poster)
  thumbnail_url?: string | null;
  original_url?: string; // admins only (unwatermarked master)
  processing_state?: 'pending' | 'ready' | 'failed';
  duration_seconds?: number; // video only
  title?: string | null;
  filename?: string;
  album_id?: Uuid;
  deliverable?: boolean;
  // Admin-only (present when serialized with :read_master). `disabled` is the
  // account-wide hidden flag; `referenced` is whether the media is in any
  // cart/order line (gates Remove/Delete — both offered only when !referenced).
  disabled?: boolean;
  referenced?: boolean;
  /** Customization-restriction tags. End-users receive only restriction-referenced
   *  keys; admins receive the full map. */
  tags?: Record<string, string>;
  // Present when the media is returned inside an Album#as_json(with_pics: true)
  // response (i.e. scoped to the album). Omitted when the media is fetched
  // without an album scope.
  direct?: boolean;
  source_album?: { id: Uuid; title: string } | null;
  // True when this media propagates to descendants — either it lives on the scope
  // album with assignment_type 'transitive', or it was inherited from an
  // ancestor (which can only happen via a transitive AlbumPic).
  transitive?: boolean;
  [key: string]: unknown;
}

export const ALBUM_TYPE_FIELD_KINDS = ['integer', 'money', 'date', 'string'] as const;
export type AlbumTypeFieldKind = (typeof ALBUM_TYPE_FIELD_KINDS)[number];

export interface AlbumTypeField {
  id: Uuid;
  album_type_id: Uuid;
  name: string;
  /** Stable machine key (`^[a-z][a-z0-9_]{0,62}$`), unique within the type. */
  key: string;
  kind: AlbumTypeFieldKind;
  position: number;
}

export type AlbumTypeSortMode = 'manual' | 'alphabetical';

/** Settings → Album types row (picsvc AlbumTypeSerializer). `albums_count` counts live albums. */
export interface AlbumType {
  id: Uuid;
  name: string;
  position: number;
  sort_mode: AlbumTypeSortMode;
  default_transitive: boolean;
  default_child_type_id: Uuid | null;
  default_needs_retouch: boolean;
  fields_count: number;
  albums_count: number;
  fields: AlbumTypeField[];
}

export interface AlbumTypeInput {
  name: string;
  sort_mode?: AlbumTypeSortMode;
  default_transitive?: boolean;
  default_child_type_id?: Uuid | null;
  default_needs_retouch?: boolean;
}
export interface AlbumTypeFieldInput { name: string; key: string; kind: AlbumTypeFieldKind }

/** A stored custom value: integer/money are numbers, date is `YYYY-MM-DD`, string is a string. */
export type AlbumCustomValue = number | string;

export interface Album {
  id: Uuid;
  name?: string;
  title?: string;
  description?: string;
  parent_id?: Uuid | null;
  account_id?: Uuid;
  access_code?: string;
  access_type?: string;
  tags?: Record<string, string>;
  pics?: Media[];
  // ISO8601 timestamp at which the presigned URLs in `pics[].url` expire.
  // Present when the response includes `pics`. Drives client-side refetch
  // scheduling so cached responses don't outlive their signatures.
  pics_url_expires_at?: string;
  direct_pics_count?: number;
  /** What a buyer sees inside (direct plus inherited, enabled only) — album cards. */
  pics_count?: number;
  /** Album cards: the first ready media's thumbnail (role-aware), absent when none. */
  cover_thumbnail_url?: string;
  /** When cover_thumbnail_url's presign expires (ISO8601). */
  cover_url_expires_at?: string;
  /** The cover media's pixel dimensions; absent when unknown. */
  cover_width?: number;
  cover_height?: number;
  published?: boolean;
  children?: Album[];
  updated_at?: string;
  /** Admin callers only (picsvc sends it when the caller may update the album). */
  album_type_id?: Uuid | null;
  /** Keyed by AlbumTypeField id; may hold dormant values of other types' fields. */
  custom_values?: Record<Uuid, AlbumCustomValue>;
  [key: string]: unknown;
}

export interface ProductCustomizationOptionValue {
  id: Uuid;
  name?: string;
  descr?: string;
  // Signed price delta applied when this value is selected. Backend serializes
  // a decimal(10,2) as a string (e.g. "5.0", "-2.0"); may also arrive as a
  // number. The frontend only displays it — totals come from the backend.
  price_modifier?: number | string;
  preview_template?: PreviewTemplate | null;
  [key: string]: unknown;
}

export interface ProductCustomizationOption {
  id: Uuid;
  name?: string;
  values?: ProductCustomizationOptionValue[];
  [key: string]: unknown;
}

export type PreviewPoint = [number, number];
export type PreviewQuad = [PreviewPoint, PreviewPoint, PreviewPoint, PreviewPoint]; // TL,TR,BR,BL, normalized 0..1
export interface PreviewSlot { quad: PreviewQuad; fit: 'cover' | 'contain'; }
export interface PreviewDescriptor { version: number; slots: PreviewSlot[]; }
export interface PreviewTemplate { base_url: string; overlay_url?: string | null; descriptor: PreviewDescriptor; }

export interface Product {
  id: Uuid;
  name?: string;
  description?: string;
  price?: number;
  album_price?: number | string;
  image_url?: string;
  digital?: boolean;
  product_customization_options?: ProductCustomizationOption[];
  preview_template?: PreviewTemplate | null;
  [key: string]: unknown;
}

/**
 * Per-locale name returned by productsvc as a jsonb hash. At least one key
 * is required (enforced backend-side). Mobility fills missing locales with
 * the requested-locale value or null, depending on configuration.
 */
export interface LocalizedName {
  en?: string | null;
  de?: string | null;
  hu?: string | null;
}

/**
 * Category shape returned by GET /product_categories (the admin/dropdown
 * endpoint). Distinct from the tree-row `ProductCategory` (which embeds
 * products). Consumed by `useProductCategories` and the admin products page.
 */
export interface ProductCategoryForAdmin {
  id: Uuid;
  name: LocalizedName;
  index: number;
  disabled: boolean;
  default_open: boolean;
  product_count: number;
}

export interface ProductCategory {
  id?: Uuid;
  name?: string;
  products: Product[];
  [key: string]: unknown;
}

/**
 * The shape returned by the product service's foralbum / forpic endpoints.
 * The backend ProductTree serializer wraps a list of categories, each with
 * an inner products array.
 */
export interface ProductTree {
  product_categories: ProductCategory[];
  [key: string]: unknown;
}

export interface ProductCustomizationSelection {
  customization_option_id: Uuid;
  customization_option_value_id: Uuid;
  // Immutable snapshot of the value's price delta at the time it was added to
  // the cart line item. Display-only; line-item totals already include it.
  price_modifier?: number | string;
  [key: string]: unknown;
}

/**
 * A pic-customization selection snapshotted onto a cart/order pic.
 *
 * `inputs` carries the user-bound inputs the customer typed/picked (keyed by
 * `PicCustomizationInputMeta.name`); `params` is the resolved snapshot echoed
 * back by pics#validate for display. `customization_option_value_id` is used
 * for options that resolve to a catalog value (e.g. a green-screen background
 * swatch). All carry an optional immutable `price_modifier` snapshot for
 * display.
 */
export interface PicCustomizationSelection {
  customization_option_id: Uuid;
  customization_option_value_id?: Uuid;
  inputs?: Record<string, string>;
  params?: Record<string, string>;
  price_modifier?: number | string;
  [key: string]: unknown;
}

/**
 * One value entry in a pic-customization option catalog. Mirrors
 * `PicCustomizationOptionValue#as_json` from pic-service.
 */
export interface PicCustomizationOptionValue {
  id: Uuid;
  name?: string;
  descr?: string;
  index?: number;
  price_modifier?: string;
  image_url?: string | null;
  [key: string]: unknown;
}

/** One input a customization type/option declares — see `PicCustomizationInputMeta`. */
export type PicCustomizationInputKind = 'text' | 'select';

/**
 * One input slot a customization type or option declares (e.g. the caption
 * producer's `row_1`/`row_2`/`placement`). `user_bound` is absent on the
 * `/types` registry payload (which describes the input shape only) and
 * present on option payloads (where it reflects whether this account bound
 * the input to a per-order user value vs. a template/static default).
 */
export interface PicCustomizationInputMeta {
  name: string;
  kind: PicCustomizationInputKind;
  required: boolean;
  user_bound?: boolean;
  options: string[];
}

/**
 * How an option's input is bound: to the request-time template context
 * (`template`), a fixed admin-configured value (`static`), or a value the
 * end-user supplies at order time (`user`). Admin payloads only.
 */
export interface PicCustomizationBinding {
  source: 'template' | 'static' | 'user';
  value?: string;
}

/**
 * One entry in the code-owned techid registry. The admin catalog create form
 * populates its techid `<select>` from this list (techid is NEVER free text).
 * `automatic` distinguishes auto-produced derived pics (bw/caption) from the
 * manual worklist sentinel. Mirrors `PicCustomizationOptionsController#types`.
 */
export interface PicCustomizationType {
  techid: string;
  automatic: boolean;
  uses_values: boolean;
  inputs: PicCustomizationInputMeta[];
}

/**
 * One option in the pic-customization catalog. Mirrors
 * `PicCustomizationOption#as_json` from pic-service.
 * `effective_tag_dependencies` is an ALL-match map of pic tags an option is
 * restricted to (empty = all pics); a `null` value means "tag present with
 * any non-empty value" rather than an exact match. `tag_dependencies` and
 * `input_bindings` are admin-payload-only (not sent to end-users).
 */
export interface PicCustomizationOption {
  id: Uuid;
  techid: string;
  /** Catalog stack position: options apply to a photo top (0) to bottom. The server lists options in this order. */
  stack_position?: number;
  effective_tag_dependencies: Record<string, string | null>;
  uses_values: boolean;
  inputs: PicCustomizationInputMeta[];
  mandatory?: boolean;
  default_selected?: boolean;
  tag_dependencies?: Record<string, string | null>;
  input_bindings?: Record<string, PicCustomizationBinding>;
  name?: string;
  descr?: string;
  values?: PicCustomizationOptionValue[];
  [key: string]: unknown;
}

/**
 * A pic as it appears inside a cart line item — typically a snapshot of the
 * pic data captured at the time the item was added to the cart.
 */
export interface CartItemPic {
  id?: Uuid;
  media_type?: MediaType;
  url?: string;
  thumbnail_url?: string;
  filename?: string;
  pic_customization?: PicCustomizationSelection[];
  [key: string]: unknown;
}

export interface CartItem {
  id: Uuid;
  quantity: number;
  album_id?: Uuid;
  product_id?: Uuid;
  product?: Product;
  pic_count?: number;
  pics?: CartItemPic[] | null;
  unit_price?: number | string;
  price?: number | string;
  product_customization?: ProductCustomizationSelection[];
  product_name?: string;
  // Cart-only promotion overlay (Cart#as_json). `unit_price`/`price` stay
  // undiscounted; these carry the live effective amounts plus the promotion
  // that produced them. Never present on Order line items.
  effective_unit_price?: number | string;
  effective_price?: number | string;
  promotion?: CartItemPromotion;
  // Order-only promotion snapshot (LineItem#as_json). `unit_price`/`price`
  // are already discounted post-freeze; these carry the pre-discount unit
  // price and the promotion name for display. Never present on Cart items.
  original_unit_price?: number | string | null;
  promotion_name?: string | null;
  [key: string]: unknown;
}

// Server-computed projected delivery range for a checkout option
// (Fulfillment::OptionProjections). min == max when the other checkout
// dimension is already selected (or for fix-date shipping); is_fix marks a
// hard-committed date rather than an estimate.
export interface ProjectedDeliveryRange {
  min_date: string; // ISO date (YYYY-MM-DD)
  max_date: string; // ISO date (YYYY-MM-DD)
  is_fix: boolean;
}

export interface ShippingOption {
  id: Uuid;
  name?: string;
  descr?: string;
  price?: number;
  image_url?: string;
  needs_address?: boolean;
  valid_for_address?: boolean;
  restricted_to_countries?: string[];
  price_by_category?: Record<string, number>;
  last_address?: ApiAddress | null;
  // ISO date string (YYYY-MM-DD) when the option commits to a fixed delivery
  // date (e.g. in-person handover at a known event). Rendered in the checkout
  // as a "Delivery date" callout.
  fix_delivery_date?: string | null;
  needs_delivery_point?: boolean;
  carrier_techid?: string;
  projected_delivery?: ProjectedDeliveryRange | null;
  [key: string]: unknown;
}

export interface DeliveryPoint {
  id: string;
  carrier: string;
  type: 'locker' | 'parcel_shop';
  name: string;
  address: {
    country?: string;
    postcode?: string;
    city?: string;
    street?: string;
  };
  location?: { lat?: number | null; lng?: number | null } | null;
  // Carriers differ: FoxPost returns a per-day object ({hetfo: "00:00-24:00", …}),
  // others may return a free-text string. Render via normalizeHours, never directly.
  hours?: string | Record<string, string> | null;
}

export interface DeliveryPointsPayload {
  shipping_option_id: Uuid;
  country?: string;
  query?: string;
}

export type PaymentOptionTechid =
  | 'cash' | 'iban' | 'custom' | 'gift'
  | 'paypal' | 'stripe' | 'barion' | 'simplepay';

export interface PaymentOption {
  id: Uuid;
  name?: string;
  descr?: string;
  price?: number;
  image_url?: string;
  info_details?: string;
  needs_billing_address?: boolean;
  techid?: PaymentOptionTechid;
  projected_delivery?: ProjectedDeliveryRange | null;
  [key: string]: unknown;
}

export interface Cart {
  id?: Uuid;
  cart_items?: CartItem[];
  line_items?: CartItem[];
  adjustment_items?: AdjustmentItem[];
  shipping_option_id?: Uuid | null;
  payment_option_id?: Uuid | null;
  shipping_option?: ShippingOption | null;
  payment_option?: PaymentOption | null;
  // True when the cart has at least one physical line item that needs shipping.
  // False for an all-digital cart — the webclient omits the shipping step.
  // Mirrors Cart#requires_shipping? from order-service.
  requires_shipping?: boolean;
  shipping_address?: ApiAddress | null;
  billing_address?: ApiAddress | null;
  items_total?: number;
  items_after_adj?: number;
  shipping_cost?: number;
  shipping_after_adj?: number;
  pre_payment_total?: number;
  payment_cost?: number;
  total?: number;
  album_ids?: Uuid[];
  // ISO date string (YYYY-MM-DD) — projected delivery date computed by the
  // backend from the selected shipping_option / payment_option (Task 10).
  // Present once both options are set; null otherwise.
  projected_delivery_date?: string | null;
  // True when the projected delivery date is a hard commitment (e.g. tied to
  // a fix_delivery_date shipping option). Available for future emphasis;
  // not currently rendered differently from the soft case in T16.
  projected_delivery_is_fix?: boolean;
  [key: string]: unknown;
}

export interface OrderPayment {
  id: Uuid;
  status?: string;
  payment_url?: string | null;
  requires_redirect?: boolean;
  redirect_url?: string | null;
  /** False when the gateway call failed — the order exists but no payment was started. */
  payment_started?: boolean;
  // String for bank transfer, null for cash/custom manual retries, undefined
  // when not applicable — a null reference on a successful manual retry is
  // not an error (#98).
  reference?: string | null;
  payment_option_id?: Uuid;
  payment_option?: PaymentOption | null;
  payment_price?: number | string;
  amount?: number | string;
  amount_received?: number | string | null;
  payment_environment?: 'sandbox' | 'live' | string;
  initiated_at?: string | null;
  completed_at?: string | null;
  /** Date the money actually reached us (bank value date, or a date an admin
   *  entered). Drives the invoice fulfillment date; null for card payments,
   *  whose completed_at already is the moment of payment. */
  settled_on?: string | null;
  order_id?: Uuid;
  [key: string]: unknown;
}

export interface OrderShipment {
  id?: Uuid;
  shipping_option?: ShippingOption | null;
  price?: number | string;
  status?: string;
  tracking_number?: string | null;
  tracking_url?: string | null;
  label_generated?: boolean;
  label_pin?: string | null;
  shipped_at?: string | null;
  delivered_at?: string | null;
  created_at?: string;
  delivery_point?: {
    id?: string;
    carrier?: string;
    type?: string;
    name?: string;
    address?: { country?: string; postcode?: string; city?: string; street?: string };
    location?: { lat?: number | null; lng?: number | null } | null;
    hours?: unknown;
  } | null;
  [key: string]: unknown;
}

export type AdjustmentKind = 'additive' | 'multiplicative';
export type AdjustmentScope = 'items_only' | 'shipping_only' | 'order_total';
export type AdjustmentSource = 'admin' | 'coupon';
export type AdjustmentReasonKind =
  | 'gift'
  | 'promotion'
  | 'goodwill'
  | 'coupon'
  | 'refund'
  | 'underpayment'
  | 'overpayment';

export interface AdjustmentItem {
  id: Uuid;
  kind: AdjustmentKind;
  amount?: number | string | null;
  multiplier?: number | string | null;
  scope: AdjustmentScope;
  reason_kind: AdjustmentReasonKind;
  reason_text?: string | null;
  source: AdjustmentSource;
  applied_by: Uuid;
  position: number;
  created_at?: string;
}

/** Order#production_clearance — whether the order may enter production, independent of payment. */
export type ProductionClearance = 'auto' | 'released' | 'held';
export const PRODUCTION_CLEARANCE_VALUES: readonly ProductionClearance[] = ['auto', 'released', 'held'];

// Manual-payment instructions for the buyer's order page (order-service
// Payments::ManualInstructions). Money arrives as a decimal string.
export interface OrderPaymentInstructions {
  techid: PaymentOptionTechid;
  text: string | null;
  box: {
    reference: string;
    amount_due: number | string;
    currency: string;
    account_holder: string | null;
    account_number: string | null;
    bank_name: string | null;
  } | null;
}

export interface Order {
  id: Uuid;
  payment_instructions?: OrderPaymentInstructions | null;
  order_number?: string;
  // The buyer (user-service user id); the admin order page links the customer to it.
  user_id?: Uuid | null;
  status?: OrderStatus;
  payment_status?: string;
  cart_items?: CartItem[];
  line_items?: CartItem[];
  adjustment_items?: AdjustmentItem[];
  shipping_option?: ShippingOption | null;
  payment_option?: PaymentOption | null;
  shipping_address?: ApiAddress | null;
  billing_address?: ApiAddress | null;
  payments?: OrderPayment[];
  order_payments?: OrderPayment[];
  order_shipments?: OrderShipment[];
  order_date?: string;
  created_at?: string;
  items_total?: number;
  items_subtotal?: number;
  items_after_adj?: number;
  shipping_cost?: number;
  shipping_subtotal?: number;
  shipping_after_adj?: number;
  pre_payment_total?: number;
  payment_cost?: number;
  payment_subtotal?: number;
  total?: number;
  total_amount?: number;
  total_paid?: number;
  refundable_amount?: number;
  currency_code?: string;
  production_status?: OrderProductionStatus;
  production_items?: OrderProductionItem[];
  // Per-supplier breakdown emitted only on the `OrdersController#show`
  // payload (Phase 11). Not present on list rows.
  production_breakdown?: ProductionBreakdownEntry[];
  // ISO date string (YYYY-MM-DD) — snapshot of the expected delivery date at
  // the moment the order was placed (T13). Recomputed when the order
  // transitions to fully paid. Null until the snapshot job has run, or for
  // orders that pre-date the time-bound-fulfillment feature.
  expected_delivery_at?: string | null;
  // Why a cancelled order was cancelled. 'unpaid_expiry' means the nightly
  // unpaid-order sweep closed it, not a human; null/absent for every other
  // cancellation (and for orders that are not cancelled at all).
  cancel_reason?: 'unpaid_expiry' | null;
  // Production clearance policy (see docs/superpowers/specs/2026-09-21-production-clearance-design.md).
  production_clearance?: ProductionClearance;
  production_cleared?: boolean;
  clearance_changed_at?: string | null;
  clearance_changed_by?: Uuid | null;
  clearance_note?: string | null;
  // True when the order's chosen shipping option is outside its validity
  // window today, so `Payments::StartForOrder` would refuse a pay-now with
  // `notify.orders.shipping_option_lapsed`. The buyer must contact the shop.
  shipping_option_lapsed?: boolean;
  [key: string]: unknown;
}

/**
 * One reason an admin acceptance point (record-payment, inline ✓, manual
 * execute) refuses to record money without an explicit confirmation
 * (order-service `Payments::AcceptanceCheck`). Returned inside the 409
 * Conflict envelope as `{ resp: { warnings: [...] }, error:
 * 'notify.orders.payment_needs_confirmation' }`; re-submitting with
 * `accept_warnings` covering every returned code proceeds (and, for
 * `order_cancelled`, reinstates the order).
 *
 * `details` keys per code:
 * - `order_cancelled`: `cancelled_at` (ISO8601 timestamp),
 *   `cancel_reason` (`'unpaid_expiry'` or null for any other cancellation).
 * - `shipping_option_lapsed`: `shipping_option_name`, `valid_until`
 *   (ISO date, or null).
 * - `order_already_paid`: `total_amount`, `total_paid`, `excess` (decimal
 *   strings or numbers depending on the serializer that emitted them).
 */
export interface PaymentAcceptanceWarning {
  code: 'order_cancelled' | 'shipping_option_lapsed' | 'order_already_paid';
  details: {
    cancelled_at?: string | null;
    cancel_reason?: string | null;
    shipping_option_name?: string | null;
    valid_until?: string | null;
    total_amount?: number | string;
    total_paid?: number | string;
    excess?: number | string;
  };
}

export interface CheckoutResponse {
  order: Order;
  payment: OrderPayment;
}

/**
 * POST /order_payments/:id/execute success body (Task 3,
 * order_payments_controller.rb#execute). Distinct from `OrderPayment` — this
 * is a small compact envelope, not the payment row. Failure is HTTP 400 with
 * `{ resp: { order_id?, outcome?, provider_ref? }, error: string }`; callers
 * read that off `ApiError.body` directly (see simplepay/Return.tsx), it isn't
 * modeled here. Keys are omitted (via Ruby's `.compact`) when absent, never
 * null.
 */
export interface ExecutePaymentResponse {
  order_id: Uuid;
  status: string;
  outcome?: 'succeeded' | 'failed' | 'cancelled' | 'timeout' | 'pending' | string;
  provider_ref?: string;
}

/**
 * Customer-facing status of one digital line item on the My Downloads page.
 * Mirrors the four tokens order-service emits — see
 * docs/superpowers/specs/2026-08-02-my-downloads-page-design.md
 */
export type DownloadStatus = 'ready' | 'awaiting_payment' | 'awaiting_retouch' | 'processing';

/** One downloadable file. `position` addresses it within its line item. */
export interface DownloadFile {
  position: number;
  /** Name the file will be saved as; null for a yearbook ISO. */
  download_filename: string | null;
  pic_title: string | null;
  thumbnail_url: string | null;
}

export interface DownloadItem {
  line_item_id: Uuid;
  product_name: string | null;
  media_type: string | null;
  status: DownloadStatus;
  /** Empty unless `status` is 'ready'. */
  files: DownloadFile[];
}

export interface DownloadOrder {
  order_id: Uuid;
  order_number: string;
  order_date: string;
  items: DownloadItem[];
}

/** The RFC 8693 `act` claim: who is really driving an impersonated session. */
export interface Impersonation {
  adminId: string;
  adminName: string;
  adminEmail: string;
  sessionId: string;
}

export interface AuthProvidersResponse {
  providers: Array<'password' | 'facebook' | string>;
  [key: string]: unknown;
}

export interface AuthTokenResponse {
  token?: string;
  idtoken?: string;
  user?: User;
  // Present on a `mfa_verify` response when the caller sent `remember_device`
  // truthy. Persisted client-side (TRUSTED_DEVICE_KEY) and replayed on the
  // next password login so a recognized device can skip the TOTP challenge.
  trusted_device_token?: string;
  [key: string]: unknown;
}

export interface MfaRequiredResponse {
  mfa_required: true;
  mfa_token: string;
  methods: string[];
}

export const isMfaRequired = (data: unknown): data is MfaRequiredResponse =>
  typeof data === 'object' &&
  data !== null &&
  (data as { mfa_required?: unknown }).mfa_required === true;

export interface AccountInfo {
  name?: string;
  /** Shop display name: Setting#web_title, or `name` when unset. Prefer this
   *  over `name` for anything a customer sees. */
  display_name?: string;
  contact_email?: string;
  contact_phone?: string;
  website?: string;
  website_url?: string;
  footer_content?: string;
  setting?: Setting;
  permissions?: string[];
  /** Absent (an older user-service) means `open`; read it through useShopState. */
  shop_state?: ShopState;
  enabled_locales?: Locale[];
  default_locale?: Locale;
  /** IANA timezone of the shop (accounts.time_zone). */
  time_zone?: string;
  [key: string]: unknown;
}

// Sentinel error code returned (403) by login and guest creation when the
// shop is not open to the requester (closed, or for guests also
// orders_paused). See docs/superpowers/specs/2026-10-22-remove-sandbox-mode-design.md §6.3.
export const SHOP_NOT_OPEN_ERROR = 'notify.auth.shop_not_open';

export interface StaticPage {
  id: Uuid;
  slug?: string;
  title?: string;
  body?: string;
  // Present on every representation the backend sends (index metadata, the
  // locale-resolved #show payload via `page.as_json`, and the raw admin
  // payload) — StaticPagesController always includes both columns.
  visibility?: StaticPageVisibility;
  position?: number;
  // Placement flags: where the shop UI lists this page (top menu / footer
  // quick links). Both false = unlisted, reachable only by direct URL.
  show_in_top_menu?: boolean;
  show_in_footer?: boolean;
  // True on the single page (unique per account) designated as the shop's
  // Terms and Conditions — may be unlisted (both placement flags false) and
  // still appear in the public index so checkout can link to it.
  terms_page?: boolean;
  // True on the single page (unique per account) designated as the shop's
  // homepage — replaces the default landing page. Mirrors terms_page.
  homepage_page?: boolean;
  // True on the single page (unique per account) designated as the shop's
  // privacy policy — linked from the code-rendered /cookies page and the
  // footer; stays in the public index even when unlisted. Mirrors terms_page.
  privacy_page?: boolean;
  // ISO8601 timestamp at which the presigned `f2m-asset://` image URLs
  // resolved into the rendered body expire. Present when the response
  // resolved asset references; drives client-side refetch scheduling so
  // cached pages don't outlive their signatures (mirrors Album#pics_url_expires_at).
  assets_expire_at?: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Static pages admin (static pages admin feature)
// ---------------------------------------------------------------------------

export type StaticPageVisibility = 'publicly_visible' | 'logged_in';

/**
 * Admin/raw shape of a static page — `GET /static_pages/:id?raw=1` and the
 * create/update mutation responses (asset_urls/assets_expire_at are omitted
 * on create/update but present on the raw GET). Per-locale title/body are
 * unresolved markdown (asset refs still `f2m-asset://<uuid>` placeholders),
 * resolved client-side via `asset_urls`.
 */
export interface StaticPageAdmin {
  id: Uuid;
  slug: string;
  visibility: StaticPageVisibility;
  show_in_top_menu: boolean;
  show_in_footer: boolean;
  // Designated Terms & Conditions page (unique per account, last write wins
  // on the backend). See StaticPage.terms_page on StaticPage above.
  terms_page: boolean;
  // True on the single page (unique per account) designated as the shop's
  // homepage — replaces the default landing page. See StaticPage.homepage_page above.
  homepage_page: boolean;
  // Designated privacy-policy page (unique per account). See StaticPage.privacy_page above.
  privacy_page: boolean;
  position: number;
  title: TranslatedString;
  body: TranslatedString;
  asset_urls?: Record<Uuid, string>;
  assets_expire_at?: string;
}

export interface StaticPageWrite {
  slug: string;
  visibility: StaticPageVisibility;
  show_in_top_menu: boolean;
  show_in_footer: boolean;
  terms_page: boolean;
  homepage_page: boolean;
  privacy_page: boolean;
  position?: number;
  title: TranslatedString;
  body: TranslatedString;
}

/** One row of the account's static-asset image library (`GET /static_assets`). */
export interface StaticAssetInfo {
  id: Uuid;
  filename: string | null;
  byte_size: number | null;
  content_type: string | null;
  created_at: string;
  url: string | null;
}

export interface GlobalSupplier {
  id: Uuid;
  techid: 'manual' | string;
  name: string;
  supplier_setting_fields?: SettingFieldSpec[];
  productsupplier_setting_fields?: SettingFieldSpec[];
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface Supplier {
  id: Uuid;
  account_id: Uuid;
  global_supplier_id: Uuid;
  global_supplier_techid: 'manual' | string;
  global_supplier_name: string;
  name: TranslatedString;
  descr: TranslatedString;
  disabled: boolean;
  processing_days: number;
  product_count?: number;
  setting_values?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ProductSupplierMapping {
  id: Uuid;
  product_id: Uuid;
  supplier_id: Uuid;
  default: boolean;
  supplier_name: string | null;
  global_supplier_techid: 'manual' | string | null;
  setting_values?: Record<string, unknown>;
}

export interface ProductSupplierMappingInput {
  supplier_id: Uuid;
  default: boolean;
  setting_values?: Record<string, unknown>;
}

export type ProductionStatus =
  | 'pending'
  | 'in_progress'
  | 'produced'
  | 'faulty';
export type OrderProductionStatus = 'pending' | 'partial' | 'all_produced';

/**
 * Canonical 8-state order status derived by `Order#status` on the backend.
 * Emitted on both list rows and the show payload.
 *
 * There is no `expired` state: an order left unpaid past its deadline is
 * CANCELLED by the nightly unpaid-order sweep, with `cancel_reason:
 * 'unpaid_expiry'` recording why (see `Order.cancel_reason` below).
 */
export type OrderStatus =
  | 'awaiting_payment'
  | 'partially_paid'
  | 'paid'
  | 'in_production'
  | 'produced'
  | 'shipped'
  | 'delivered'
  | 'cancelled';

/**
 * Production status vocabulary used by the new admin orders list-row payload
 * (`OrdersController#serialize_order_list_row`, Phase 11). Distinct from
 * `OrderProductionStatus` above which is the slice-1 vocabulary still emitted
 * by `Order#as_json` on the show endpoint. Phases 16-18 will migrate the badge
 * + show payload onto this vocabulary, at which point the two can collapse.
 */
export type OrderListRowProductionStatus =
  | 'not_started'
  | 'partial'
  | 'completed';

/**
 * One row of the admin orders list (`GET /direct/ordersvc/orders`). Modelled
 * exactly after `OrdersController#serialize_order_list_row`. Strictly a
 * subset of `Order` fields — does NOT include line_items or addresses.
 * `orderApi.getOrders` returns `Paginated<OrderListRow>`; every consumer
 * (Orders.tsx, AdminOrders.tsx, AdminUserDetail via useAdminUserOrders)
 * reads `.items`.
 */
export interface OrderListRow {
  id: Uuid;
  order_number: string;
  order_date: string | null;
  customer_name: string | null;
  item_count: number;
  total_amount: number | string;
  currency_code: string;
  payment_status: string;
  production_status: OrderListRowProductionStatus;
  status: OrderStatus;
  // Same field as `Order.cancel_reason`, emitted on list rows too
  // (OrdersController#serialize_order_list_row) so the admin list can tell a
  // swept order from an admin cancellation without opening it.
  cancel_reason?: 'unpaid_expiry' | null;
  expected_delivery_at: string | null;
  production_clearance: ProductionClearance;
}

/**
 * One supplier-grouped entry in `Order#show`'s `production_breakdown` array.
 * Produced by `Fulfillment::OrderProductionBreakdown` (Phase 11). Note that
 * `supplier_name` is currently OMITTED from the backend payload — neither
 * `SupplierSettingsClient` nor `DefaultSupplierLookup` returns the display
 * name; the frontend resolves it via productsvc. See plan deviations
 * Phase 10/11 for context. Typed as optional to honestly reflect runtime.
 */
/** Compact reference to an order, used in cross-reference UI (links). */
export interface OrderRef {
  id: Uuid;
  order_number: string;
}

export interface ProductionBreakdownEntry {
  supplier_id: Uuid;
  supplier_techid: string;
  supplier_name?: string;
  supplier_kind: 'system' | 'external' | 'manual';
  status: 'not_started' | 'in_batch' | 'partial' | 'completed';
  line_item_ids: Uuid[];
  batch_id?: Uuid;
  batch_status?: 'prepared' | 'in_progress' | 'completed' | 'error';
  production_group_id?: Uuid;
  also_covers_orders?: OrderRef[];
  // Only emitted for admin callers; if webclient ever deploys before
  // ordersvc, this field is absent — every render site must guard with
  // `(entry.production_items ?? [])`.
  production_items?: ProductionBreakdownProductionItem[];
}

/**
 * One unique production item embedded in a `ProductionBreakdownEntry`
 * (deduped server-side — a compound PI serving several line items appears
 * once). Per `Fulfillment::OrderProductionBreakdown#production_item_payload`.
 */
export interface ProductionBreakdownProductionItem {
  id: Uuid;
  status: ProductionStatus;
  quantity: number;
  /** Set when generating the line's production media failed (spec §9). */
  generation_error?: string | null;
  /** With `generation_error`, tells an empty `production_media` apart — see `emptyMediaPlaceholder`. */
  sources?: ProductionItemSource[];
  production_media: ProductionMediaFile[];
}

/**
 * Embedded PI summary returned inside Order#as_json's `production_items` array.
 * Per order-service `Order#serialize_production_items` (branch 3).
 */
export interface OrderProductionItem {
  id: Uuid;
  line_item_id: Uuid;
  product_id: Uuid;
  supplier_id: Uuid;
  status: ProductionStatus;
  quantity: number;
  produced_quantity: number;
  faulty_quantity: number;
  started_at: string | null;
  produced_at: string | null;
  // True when this PI is produced AND its line item's product is digital, so
  // the customer can download the deliverable (Plan 3 / video-support).
  downloadable?: boolean;
}

/** One decoded customization layered onto a `ProductionItemSource` (detail view). Per
 * `ProductionItemsController#sources_payload`. `value_id` is `null` for a
 * value-less option (e.g. a caption) — ruling 2026-10-02.
 */
export interface ProductionItemSourceCustomization {
  option_id: Uuid;
  value_id: string | null;
  params: Record<string, unknown>;
}

/**
 * A `GET /production_items/:id` source: the shared `ProductionItemSource`
 * shape plus its decoded customizations, in signature order. Per
 * `ProductionItemsController#sources_payload`.
 */
export interface ProductionItemDetailSource extends ProductionItemSource {
  customization_signature: string | null;
  customizations: ProductionItemSourceCustomization[];
}

/**
 * Full PI returned by GET /production_items/:id and by the start/produce
 * mutations. Per order-service `ProductionItemsController#serialize` (branch 3).
 */
export interface ProductionItem {
  id: Uuid;
  account_id: Uuid;
  product_id: Uuid;
  supplier_id: Uuid;
  quantity: number;
  produced_quantity: number;
  faulty_quantity: number;
  status: ProductionStatus;
  sources: ProductionItemDetailSource[];
  product_customizations: Array<{ option_id: Uuid; value_id: Uuid }>;
  reproduction_id?: Uuid | null;
  started_at: string | null;
  produced_at: string | null;
  line_items: Array<{ id: Uuid; order_id: Uuid | null; quantity: number }>;
}

// ---------------------------------------------------------------------------
// Production pic descriptor (per-product transformation)
// ---------------------------------------------------------------------------

export type DescriptorPicMethod = 'expand' | 'indifferent_crop' | 'rotate_then_crop';
export type DescriptorPicOrientation = 'match_source' | 'strict';

export interface ResizeDescriptor {
  method: 'resize';
  boxsize: number;
  dpi?: number;
}

export interface ComposeDescriptor {
  method: 'compose';
  width: number;
  height: number;
  count: number;
  pic: { width: number; height: number; method: DescriptorPicMethod; orientation?: DescriptorPicOrientation };
  dpi?: number;
}

export type Descriptor = null | Record<string, never> | ResizeDescriptor | ComposeDescriptor;

// ---------------------------------------------------------------------------
// Product admin types (Task 16)
// ---------------------------------------------------------------------------

export type SettingFieldType = 'string' | 'secret' | 'boolean' | 'number' | 'enum';

export interface SettingFieldSpec {
  id: number;
  name: string;
  mandatory: boolean;
  type: SettingFieldType;
  options?: string[];
}

/**
 * Admin-specific product shape returned by productsvc's admin endpoints.
 * Includes all editable fields not present in the shop-facing `Product` type.
 *
 * `name` / `descr` are nested per-locale hashes — see `TranslatedString`.
 * The backend emits them via `TranslatableAttributes.serialize`; locales
 * outside the account's enabled set are still present as keys but with `null`
 * values.
 */
export interface ProductForAdmin {
  id: Uuid;
  name: TranslatedString;
  descr: TranslatedString;
  digital: boolean;
  index: number;
  media_count_min: number;
  media_count_max: number;
  shipping_category: string;
  product_category_id: Uuid;
  disabled: boolean;
  deleted_at?: string | null;
  /**
   * False when the product has no supplier mapping at all — it cannot be
   * produced. Optional: absent when talking to a product-service that predates
   * the field, so only an explicit `false` should trigger UI warnings.
   */
  has_supplier?: boolean;
  production_pic_descriptor?: Descriptor;
  allowed_orientations: PictureOrientation[];
  deliverable?: ProductDeliverable | null;
  // serialize_admin merges into Product#as_json, so the admin payload also
  // carries the customer-facing fields — including the product's own preview
  // template and the option tree whose values each carry theirs.
  preview_template?: PreviewTemplate | null;
  product_customization_options?: AdminCustomizationOption[];
}

/**
 * One value of an admin customization option, as returned by the
 * customization_options/values admin CRUD endpoints (Tasks 3-4). `name` /
 * `descr` are nested per-locale hashes (see `TranslatedString`), unlike the
 * buyer-facing `ProductCustomizationOptionValue`.
 */
export interface AdminCustomizationValue {
  id: Uuid;
  index: number;
  techid: string | null;
  price_modifier: string;
  name: TranslatedString;
  descr: TranslatedString;
  preview_template?: PreviewTemplate | null;
}

/**
 * One customization option of a `ProductForAdmin`, as returned by the
 * customization_options admin CRUD endpoints (Tasks 3-4). `name` / `descr`
 * are nested per-locale hashes (see `TranslatedString`), unlike the
 * buyer-facing `ProductCustomizationOption`.
 */
export interface AdminCustomizationOption {
  id: Uuid;
  mandatory: boolean;
  techid: string;
  name: TranslatedString;
  descr: TranslatedString;
  values: AdminCustomizationValue[];
}

/**
 * Points a pictureless+digital product at an external content source it
 * fulfills by generating a download rather than a physical/manual supplier
 * flow. Currently only `yearbook_iso`. See product-service's
 * `Product#deliverable_shape` for the backend invariants (must be digital,
 * media_count_min/max both 0, yearbook_id present). The admin API may also
 * return `{}` for products with no deliverable set (Product#deliverable
 * reads through to `{}`, never nil) — narrow on `type` before use.
 */
export interface ProductDeliverable {
  type: 'yearbook_iso';
  yearbook_id: Uuid;
}

export interface ProductCreatePayload {
  name: TranslatedString;
  descr: TranslatedString;
  digital: boolean;
  media_count_min: number;
  media_count_max: number;
  shipping_category: string;
  product_category_id: Uuid;
  disabled: boolean;
  production_pic_descriptor?: Descriptor;
  allowed_orientations: PictureOrientation[];
  deliverable?: ProductDeliverable | null;
}

export type ProductUpdatePayload = Partial<ProductCreatePayload>;

export interface ProductDeleteResult {
  deleted: 'soft' | 'hard';
}

export interface LineItemCount {
  count: number;
}

// ---------------------------------------------------------------------------
// Production batches (Phase 9 admin endpoints)
// ---------------------------------------------------------------------------

export type ProductionBatchStatus = 'prepared' | 'in_progress' | 'completed' | 'error';

/**
 * A generated production file embedded inside a `ProductionBatchProductionItem`.
 * Per `Fulfillment::ProductionMediaPayload`. `url` is a short-lived presigned
 * URL straight to Garage (not a relative path, post media-generic production).
 *
 * `position` is the 0-based index of this file within its PI's media list. The
 * backend orders the array by position, but consumers that care about a
 * specific order should sort defensively.
 */
export interface ProductionMediaFile {
  id: Uuid;
  position: number;
  attached: boolean;
  url: string | null;
  content_type: string | null;
  /** ProductionItemSource ids this file was made from. */
  source_ids: Uuid[];
}

/**
 * One media slot of a production line (`production_item_sources`). Per
 * `ProductionBatchSerialization#serialize_pi`.
 */
export interface ProductionItemSource {
  id: Uuid;
  position: number;
  media_id: Uuid;
  media_type: string;
}

/** Media = the picture/video/file itself; product = the producer's side (the physical item). */
export type FaultyKind = 'media' | 'product';

/**
 * A reported fault on a production line (spec §7). `waiting` marks a
 * `reproduce` fault not yet adopted by a production — still editable
 * (withdraw/update).
 */
export interface ProductionFault {
  id: Uuid;
  kind: FaultyKind;
  quantity: number;
  resolution: 'reproduce' | 'regenerate';
  fix_note: string | null;
  waiting: boolean;
}

/**
 * One PI row in a batch detail's `production_items` array. Per
 * `ProductionBatchSerialization#serialize_pi`. Each PI carries an ordered
 * list of `ProductionMediaFile` entries — one per input source for fan-out
 * producers, or potentially zero/many for future composite descriptors.
 */
export interface ProductionBatchProductionItem {
  id: Uuid;
  product_id: Uuid;
  /**
   * Product display name. A nested `TranslatedString`: the batch serializer
   * forwards the admin product payload from product-service, whose translated
   * attributes are serialized via `TranslatableAttributes`. Localize with
   * `pickLocalized` before rendering.
   */
  product_name: TranslatedString | null;
  customization: Record<string, string>;
  quantity: number;
  produced_quantity: number;
  faulty_quantity: number;
  status: ProductionStatus;
  production_media: ProductionMediaFile[];
  /**
   * The line's input media slots. Per `ProductionBatchSerialization#serialize_pi`
   * (always present on live responses — Task 13 omitted it from this type;
   * added back here since `Thumbnails` needs it to tell "nothing to produce
   * yet" from "still generating" when `production_media` is empty). Optional
   * so existing fixtures that predate it don't all need updating.
   */
  sources?: ProductionItemSource[];
  contributing_orders: OrderRef[];
  sorting_number?: string | null;
  metadata?: Record<string, unknown>;
  // The next fields are always present on live responses (serialize_pi
  // unconditionally includes them) but marked optional, matching
  // sorting_number/metadata above, so existing fixtures that predate them
  // don't all need updating.
  /** e.g. "000-5" — a reproduction carries (and ships under) the original's label. */
  label?: string | null;
  /** This line's media/customization type ("photo", ...); null on legacy lines. */
  media_type?: string | null;
  /** The line's current fault report, if any. */
  fault?: ProductionFault | null;
  /** Whether a media fault can be reported against this line's media type. */
  media_fault_allowed?: boolean;
  /** The line's media are generated, or it has no sources (drives the detail page's polling). */
  media_ready?: boolean;
  /** Media generation failure message, if any; null otherwise. */
  generation_error?: string | null;
  /** The reproduction made of this line's faulty units, once adopted by a production. */
  reproduction?: ReproductionLink | null;
  /** On a reproduction line: the faulty line it redoes. */
  reproduction_of?: ReproductionLink | null;
}

/**
 * The other end of a faulty <-> reproduction link (`ProductionBatchSerialization#link_to`).
 * `group_id`/`group_sorting_number` are null while the reproduction still waits for
 * the next production (no batch/group yet) — adopted only; see `ProductionFault.waiting`
 * / `WaitingReproduction` for the not-yet-adopted case.
 */
export interface ReproductionLink {
  id: Uuid;
  group_id: Uuid | null;
  group_sorting_number: string | null;
  status: ProductionStatus;
  quantity: number;
}

/**
 * A reproduction waiting for the next production to adopt it. Per
 * `Fulfillment::WaitingReproductions::Entry#as_payload`. `id` is the fault's
 * id — what `POST /production_groups` takes in `reproduction_ids`.
 */
export interface WaitingReproduction {
  id: Uuid;
  label: string | null;
  product_id: Uuid;
  supplier_id: Uuid;
  quantity: number;
  reason: FaultyKind;
  note: string | null;
  ready: boolean;
  block_reason: 'awaiting_fix' | 'awaiting_derivative' | null;
  original_group_id: Uuid | null;
  order_numbers: string[];
}

/**
 * Common fields shared by batch index rows and detail payloads. Per
 * `ProductionBatchSerialization#serialize_batch` (base shape).
 */
export interface ProductionBatch {
  id: Uuid;
  name: string;
  supplier_id: Uuid;
  supplier_techid: string | null;
  supplier_name: string | null;
  global_supplier_name: string | null;
  status: ProductionBatchStatus;
  created_at: string;
  metadata: Record<string, unknown>;
  production_items_count: number;
  production_media_ready: number;
  quantity_total: number;
  produced_quantity_total: number;
  has_error: boolean;
  /**
   * Which output producer runs this batch — drives the in-house "Kész"
   * action (only shown for `manual`). Null only on batches of migrated
   * legacy orders, which the photographer's production lists leave out.
   */
  producer_kind?: 'system' | 'external' | 'manual' | null;
}

/**
 * Detail payload returned by `GET /production_batches/:id` and the create/
 * mark_produced mutations: base batch + embedded PI list.
 *
 * `output_zip_url` is a short-lived absolute presigned URL straight to
 * Garage object storage for the `:manual` ZIP download. It is `null`
 * until the background producer attaches the ZIP. Only `:manual` batches
 * ever populate it; other kinds will see `null`.
 *
 * `output_zip_url_expires_at` is the ISO8601 timestamp at which the
 * presigned URL becomes invalid (server-side `Time.current + image_url_expiry`
 * at serialization time). `null` whenever `output_zip_url` is null.
 */
export interface ProductionBatchDetail extends ProductionBatch {
  production_items: ProductionBatchProductionItem[];
  output_zip_url: string | null;
  output_zip_url_expires_at: string | null;
}

/**
 * `completed_reproductions_pending` overlays `completed` when every batch is
 * done but a fault reproduction from this group still waits for the next
 * production (`ProductionGroupSerialization#group_status_with_pending`,
 * spec §7.7).
 */
export type ProductionGroupStatus =
  | 'prepared'
  | 'in_progress'
  | 'completed'
  | 'error'
  | 'completed_reproductions_pending';

/**
 * Compact supplier reference embedded in group summary payloads so the list
 * view can show a per-group supplier column without extra round-trips. Mirrors
 * `ProductionGroupSerialization#group_suppliers`.
 */
export interface GroupSupplierRef {
  id: Uuid;
  // Scalar (Mobility default-locale read): order-service's group_suppliers
  // helper resolves the supplier via productsvc's public show endpoint
  // (`serialize_public`), which returns `supplier.name` as a plain string,
  // not the nested TranslatedString that `serialize_admin` emits.
  name: string | null;
  global_supplier_name: string | null;
}

export interface ProductionGroup {
  id: string;
  /** What the photographer typed when starting it; null when left blank. */
  name: string | null;
  sorting_number: string | null;
  status: ProductionGroupStatus;
  created_at: string;
  production_batches_count: number;
  production_items_count: number;
  quantity_total: number;
  /**
   * Distinct orders behind the group's PHYSICAL items (reprints count their
   * original's orders). Only present when the row was built from
   * `Fulfillment::GroupListStats` — the photographer's kind-filtered list
   * (`?kind=physical|digital`) and every `full: true` (detail/create) payload
   * always have it.
   */
  orders_count?: number;
  suppliers: GroupSupplierRef[];
}

export interface ProductionGroupDetail extends ProductionGroup {
  production_batches: ProductionBatchDetail[];
}

/**
 * Async-job acknowledgement returned by `POST /production_batches/:id/run_producer`
 * (HTTP 202). Mirrors `{ id, status: "queued" }` payload.
 */
export interface ProductionBatchQueuedAck {
  id: Uuid;
  status: 'queued';
}

// ---------------------------------------------------------------------------
// Production queue (Phase 10 admin endpoint)
// ---------------------------------------------------------------------------

/**
 * Per-order producibility rolled up from line items.
 * 'fully'     — all LIs are producible
 * 'partially' — at least one LI is producible, at least one is blocked
 * 'none'      — no LIs are producible (edge case, not normally shown)
 */
export type Producibility = 'fully' | 'partially' | 'none';

/** Per-LI producibility: blocked if the attached pic is not deliverable. */
export type LiProducibility = 'producible' | 'blocked';

/**
 * Why a BLOCKED line item is not producible. Present only on blocked LIs.
 * - awaiting_fix: a source media still needs a fix (not yet deliverable).
 * - awaiting_composite: a manual derived media (e.g. green-screen) is on the
 *   photographer worklist, not produced yet.
 * - awaiting_derivative: an automatic derived media (b&w/caption) is still being
 *   generated by libvips — momentary, no human worklist (Slice 3).
 */
export type LiBlockReason = 'awaiting_fix' | 'awaiting_composite' | 'awaiting_derivative';

/** Minimal LI descriptor embedded in the production queue order summary. */
export interface ProducibleOrderLineItem {
  id: Uuid;
  /** Ordered quantity of this line item. */
  quantity: number;
  producibility: LiProducibility;
  /** Why this LI is blocked. Present only when producibility === 'blocked'. */
  block_reason?: LiBlockReason | null;
  /** ISO-8601 timestamp of the most recent SystemFulfillmentJob attempt
   *  for this LI. null when the auto job has never run for this LI. Only
   *  populated for system-kind groups in practice. */
  last_auto_fulfill_attempted_at?: string | null;
  /** Last error message from SystemFulfillmentJob (truncated to 500 chars). */
  last_auto_fulfill_error?: string | null;
}

/**
 * One order summary inside a producible-supplier group. Per
 * `ProductionQueueController#serialize_order_summary`.
 */
export interface ProductionQueueProducibleOrder {
  id: Uuid;
  order_number: string;
  order_date: string | null;
  customer_name: string | null;
  item_count: number;
  total: number | string;
  /** Rolled-up producibility for the order. Present when the queue is fetched
   *  with `producibility=partial`; may be absent on fully-only fetches. */
  producibility?: Producibility;
  /** Per-LI producibility details. Present when `producibility=partial`. */
  line_items?: ProducibleOrderLineItem[];
}

/**
 * One supplier group inside `ProductionQueue#producible`. Per
 * `ProductionQueueController#group_by_supplier`. `supplier_name` is
 * intentionally omitted by the backend (Phase 10 deviation); resolve via
 * productsvc on the frontend.
 */
export interface ProductionQueueProducibleGroup {
  supplier_id: Uuid;
  supplier_techid: string;
  supplier_name: string | null;
  global_supplier_name: string | null;
  kind: 'manual' | 'external' | 'system';
  orders: ProductionQueueProducibleOrder[];
}

/**
 * Payload returned by `GET /direct/ordersvc/production_queue`. Per
 * `ProductionQueueController#show`.
 */
export interface ProductionQueue {
  producible: ProductionQueueProducibleGroup[];
  reproductions: WaitingReproduction[];
}

/**
 * Payload returned by `GET /direct/ordersvc/production_queue/summary` — the
 * cheap, no-payload counts behind the production page's "N orders (M
 * products) + K reproductions (U products)" line (spec §4.1). Per
 * `ProductionQueueController#summary`.
 */
export interface QueueSummary {
  orders: number;
  units: number;
  reproductions: number;
  reproduction_units: number;
}

export type RetouchBucket = 'cleared' | 'uncleared';
/** Pinned to common-contracts/retouch_bucket.json (see retouchBucket.test.ts). */
export const RETOUCH_BUCKETS: readonly RetouchBucket[] = ['cleared', 'uncleared'];
export const DEFAULT_RETOUCH_BUCKET: RetouchBucket = 'cleared';
export type RetouchDeadlineSource = 'snapshot' | 'fallback';

/** Fields of the order a queue pic was pulled in by (Admin::RetouchController#pics). */
export interface RetouchQueueOrderFields {
  order_id: Uuid;
  order_number: string;
  /** ISO date; null when the order has no delivery promise. */
  retouch_deadline_at: string | null;
  deadline_source: RetouchDeadlineSource;
}

export interface RetouchPic extends Partial<RetouchQueueOrderFields> {
  id: Uuid;
  url: string;
  title?: string | null;
  deliverable: boolean;
}

export interface RetouchOrderRow {
  id: Uuid;
  order_number: string;
  customer_email: string | null;
  order_date: string | null;
  expected_delivery_at: string | null;
  retouch_deadline_at: string | null;
  deadline_source: RetouchDeadlineSource;
  total_amount: string;
  currency_code: string;
  status: string;
  pic_ids: Uuid[];
  pics: RetouchPic[];
}

export type RetouchBatchStatus =
  | 'creating'
  | 'build_failed'
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'deleting';

export interface RetouchBatchPic {
  id: Uuid;
  url: string;
  title: string | null;
  deliverable: boolean;
  versions_count: number;
  /** The pic's latest media hold (open one first); null when it never had one. */
  hold: {
    source: string;
    request_source: string | null;
    requested_at: string | null;
    note: string | null;
  } | null;
}

export interface RetouchBatch {
  id: Uuid;
  account_id: Uuid;
  user_id: Uuid;
  status: RetouchBatchStatus;
  pic_ids: Uuid[];
  pic_count: number;
  /** Pictures of the package already deliverable — the row's "20 / 40". */
  done_count: number;
  /** Set on a create resolved by a walk (Check out N): how many orders started a group. */
  order_count?: number;
  /** Set on a create resolved by the album walk: how many distinct albums the pictures came from. */
  album_count?: number;
  ready_at: string | null;
  created_at: string;
  zip_build_error_message: string | null;
  output_zip_url?: string;
  output_zip_url_expires_at?: string;
  pics?: RetouchBatchPic[];
}

export type RetouchUploadStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'partially_completed'
  | 'failed';

export interface RetouchUploadResultEntry {
  filename: string;
  pic_id: Uuid | null;
  status: 'ok' | 'skipped' | 'error';
  error?: string;
  affected_rb_ids?: Uuid[];
}

export interface RetouchUpload {
  id: Uuid;
  account_id: Uuid;
  user_id: Uuid;
  status: RetouchUploadStatus;
  original_filename: string | null;
  byte_size: number | null;
  entry_count: number | null;
  success_count: number;
  error_count: number;
  results: RetouchUploadResultEntry[] | null;
  error_message: string | null;
  processed_at: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Customization admin types (Slice 1)
// ---------------------------------------------------------------------------

export type CustomizationBatchStatus =
  | 'creating'
  | 'build_failed'
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'deleting';

export interface CustomizationBatch {
  id: Uuid;
  account_id: Uuid;
  user_id: Uuid;
  status: CustomizationBatchStatus;
  item_count: number;
  /** Set on a create resolved by the order walk (Start N by orders): how many orders it took. */
  order_count?: number;
  ready_at: string | null;
  created_at: string;
  zip_build_error_message: string | null;
  output_zip_url?: string;
  output_zip_url_expires_at?: string;
  items?: CustomizedMediaItem[];
}

export type CustomizedMediaState = 'pending' | 'produced';

/** One dereferenced customization selection on a pending composite (for display). */
export interface CustomizedMediaSelection {
  option_name: string;
  value_name: string;
  value_image_url?: string | null;
  params?: Record<string, unknown>;
}

export interface CustomizedMediaItem {
  id: Uuid;
  source_media_id: Uuid;
  customization_signature: string;
  customization_batch_id: Uuid | null;
  state: CustomizedMediaState;
  produced_at: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  thumbnail_url?: string | null;
  /** Best-effort human-readable title of the source pic (album title, else filename). */
  source_pic_title?: string | null;
  /** Selected options/backgrounds with names + image, dereferenced from the signature. */
  selections?: CustomizedMediaSelection[];
}

/** A (source pic, signature) pair — the unit of the Variants queue. */
export interface CustomizationPair {
  source_media_id: Uuid;
  customization_signature: string;
}

/** Fields of the order a queue item was pulled in by (Admin::CustomizationsController#items). */
export interface CustomizationQueueOrderFields {
  order_id: Uuid;
  order_number: string;
  /** ISO date; null when the order has no delivery promise. */
  deadline_at: string | null;
  deadline_source: RetouchDeadlineSource;
}

/**
 * One queue item. `id` is null for a VIRTUAL item — a pair with no
 * CustomizedMedia row yet; the row is created when a batch is started. Select
 * and key by `key`, never by `id`.
 */
export interface CustomizationQueueItem extends Partial<CustomizationQueueOrderFields> {
  id: Uuid | null;
  key: string;
  source_media_id: Uuid;
  customization_signature: string;
  thumbnail_url?: string | null;
  source_pic_title?: string | null;
  selections?: CustomizedMediaSelection[];
}

export interface CustomizationOrderRow {
  id: Uuid;
  order_number: string;
  customer_email: string | null;
  order_date: string | null;
  expected_delivery_at: string | null;
  deadline_at: string | null;
  deadline_source: RetouchDeadlineSource;
  total_amount: string;
  currency_code: string;
  status: string;
  items: CustomizationQueueItem[];
}

export type CustomizationUploadStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'partially_completed'
  | 'failed';

export interface CustomizationUploadResultEntry {
  filename: string;
  customized_media_id: Uuid | null;
  status: 'ok' | 'skipped' | 'error';
  error?: string;
  affected_cb_ids?: Uuid[];
}

export interface CustomizationUpload {
  id: Uuid;
  account_id: Uuid;
  user_id: Uuid;
  status: CustomizationUploadStatus;
  original_filename: string | null;
  byte_size: number | null;
  entry_count: number | null;
  success_count: number;
  error_count: number;
  results: CustomizationUploadResultEntry[] | null;
  error_message: string | null;
  processed_at: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Admin user management types (Task 14)
// ---------------------------------------------------------------------------

/**
 * One row returned by `GET /direct/usersvc/admin/users`.
 * Mirrors `AdminUserSerializer.row`.
 */
export interface AdminUserRow {
  id: Uuid;
  name: string;
  email: string;
  email_verified: boolean;
  last_login_at: string | null;
  provider: string | null;
  roles: string[];
  guest: boolean;
  disabled_at: string | null;
  created_at: string;
}

/**
 * Full user detail returned by `GET /direct/usersvc/admin/users/:id`.
 * Mirrors `AdminUserSerializer.detail` (row + `email_verified_at`).
 */
export interface AdminUserDetail extends AdminUserRow {
  email_verified_at: string | null;
}

// PATCH /admin/users/:id/email_unverified: the detail plus whether the
// (throttled) verification mail went out, and if not, when it may.
export interface AdminEmailUnverifiedResult extends AdminUserDetail {
  verification_sent: boolean;
  retry_after_seconds: number | null;
}

// ---------------------------------------------------------------------------
// Relations (relationsvc) — docs/superpowers/specs/2026-09-06-relations-design.md
// ---------------------------------------------------------------------------

export type PartnerFieldKind = 'text' | 'number' | 'date' | 'url';
export const PARTNER_FIELD_KINDS: readonly PartnerFieldKind[] = ['text', 'number', 'date', 'url'];

export interface PartnerFieldDefinition {
  id: Uuid;
  name: string;
  kind: PartnerFieldKind;
  required: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface PartnerFieldDefinitionInput {
  name: string;
  kind: PartnerFieldKind;
  required: boolean;
}

export interface Partner {
  id: Uuid;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  /** `{ [definitionId]: value }` — always strings, blanks are absent. */
  custom_values: Record<Uuid, string>;
  contacts_count: number;
  /** Ids of required definitions this partner has no value for. */
  missing_required_fields: Uuid[];
  last_discussion_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PartnerInput {
  name: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
  custom_values: Record<Uuid, string>;
}

export interface Contact {
  id: Uuid;
  partner_id: Uuid;
  name: string;
  email: string | null;
  phone: string | null;
  /** Job title, free text. */
  position: string | null;
  /** Linked shop user (user-service id), or null. Unique per account. */
  user_id: Uuid | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactInput {
  name: string;
  email: string;
  phone: string;
  position: string;
  notes: string;
}

/**
 * PATCH /contacts/:id body. The link is edited outside the contact form, so
 * every field is optional; `user_id: null` unlinks.
 */
export type ContactUpdateInput = Partial<ContactInput> & { user_id?: Uuid | null };

/** GET /contacts?user_id= — the linked contact plus its partner's name. */
export interface ContactWithPartner extends Contact {
  partner_name: string | null;
}

export type DiscussionChannel = 'phone' | 'email' | 'in_person' | 'other';
export const DISCUSSION_CHANNELS: readonly DiscussionChannel[] = ['phone', 'email', 'in_person', 'other'];

export interface Discussion {
  id: Uuid;
  partner_id: Uuid;
  contact_id: Uuid | null;
  /** UTC instant; render in the shop time zone. */
  happened_at: string;
  channel: DiscussionChannel;
  /** Markdown (inline marks only). */
  body: string;
  created_by_id: Uuid;
  created_at: string;
  updated_at: string;
}

export interface DiscussionInput {
  contact_id: Uuid | null;
  happened_at: string;
  channel: DiscussionChannel;
  body: string;
}

/** What a delete would cascade to; only the kinds that exist for the resource are present. */
export type DeletionImpact = Partial<Record<
  'contacts' | 'discussions' | 'tasks' | 'events' | 'partners_with_value' | 'albums' | 'trashed_albums' | 'albums_with_value',
  number
>>;

export type RelationsTypeKind = 'task_types' | 'event_types';

/** A TaskType or EventType row — same shape for both kinds. */
export interface RelationsType {
  id: Uuid;
  name: string;
  /** `#rrggbb`, lowercase. */
  color: string;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface RelationsTypeInput {
  name: string;
  color: string;
}

export type TaskStatus = 'open' | 'started' | 'done' | 'not_done' | 'blocked';
export const TASK_STATUSES: readonly TaskStatus[] = ['open', 'started', 'done', 'not_done', 'blocked'];

export type TaskDisplayStatus = 'not_started' | 'ready' | 'past_due' | 'started' | 'started_past_due' | 'done' | 'not_done' | 'blocked';
export const TASK_DISPLAY_STATUSES: readonly TaskDisplayStatus[] = [
  'not_started', 'ready', 'past_due', 'started', 'started_past_due', 'done', 'not_done', 'blocked',
];

// Recurrence rule — pinned in common-contracts/relations_recurrence.json.
export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';
export const RECURRENCE_FREQUENCIES: readonly RecurrenceFrequency[] = ['daily', 'weekly', 'monthly', 'yearly'];

export type RecurrenceEnds = 'never' | 'count' | 'until';
export const RECURRENCE_ENDS: readonly RecurrenceEnds[] = ['never', 'count', 'until'];

export const RECURRENCE_INTERVAL_MAX = 99;
export const RECURRENCE_COUNT_MAX = 500;
export const RECURRENCE_MAX_REMINDERS = 3;

/** The rule as an occurrence row carries it, for "Repeats every 2 weeks". */
export interface RecurrenceSummary {
  frequency: RecurrenceFrequency;
  interval: number;
  weekdays: number[];
  month_day: number | null;
  ends: RecurrenceEnds;
  count: number | null;
  until_at: string | null;
}

/** Which occurrences a save or delete applies to. */
export type RecurrenceScope = 'this' | 'following' | 'all';

export interface RecurrenceRuleInput {
  frequency: RecurrenceFrequency;
  interval: number;
  /** Weekly only; 0 = Monday … 6 = Sunday. */
  weekdays: number[];
  /** Monthly only; 1..31, clamped to the month's length. */
  month_day: number | null;
  ends: RecurrenceEnds;
  count: number | null;
  /** With ends = 'until'; shop-local 23:59 of the chosen day, as an instant. */
  until_at: string | null;
}

export interface RecurrenceTaskTemplate {
  task_type_id: Uuid;
  title: string;
  notes: string;
  assignee_id: Uuid | null;
  partner_id: Uuid | null;
  contact_id: Uuid | null;
  discussion_id: Uuid | null;
  reminders: number[];
}

export interface RecurrenceEventTemplate {
  event_type_id: Uuid;
  title: string;
  notes: string;
  attendee_ids: Uuid[];
  partner_id: Uuid | null;
  contact_id: Uuid | null;
  discussion_id: Uuid | null;
  reminders: number[];
}

export interface RecurrenceInput extends RecurrenceRuleInput {
  kind: 'task' | 'event';
  /** The account's IANA zone, captured at save time. */
  time_zone: string;
  /** 'YYYY-MM-DDTHH:mm' wall clock of the first occurrence's anchor. */
  local_start: string;
  /** Events: ends − starts. */
  duration_minutes: number | null;
  /** Tasks with a start and a due: due − start. */
  due_offset_minutes: number | null;
  /** Tasks whose anchor is the due instant (no start). */
  anchor_is_due: boolean;
  all_day: boolean;
  template: RecurrenceTaskTemplate | RecurrenceEventTemplate;
}

export interface Recurrence extends RecurrenceRuleInput {
  id: Uuid;
  kind: 'task' | 'event';
  time_zone: string;
  local_start: string;
  duration_minutes: number | null;
  due_offset_minutes: number | null;
  anchor_is_due: boolean;
  all_day: boolean;
  template: RecurrenceTaskTemplate | RecurrenceEventTemplate;
  generated_count: number;
  materialized_until: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
}

/** POST /recurrences and PATCH /recurrences/:id both answer with this. */
export interface RecurrenceCreated<T> {
  recurrence: Recurrence;
  first: T | null;
}

/**
 * Reminder offsets, in minutes BEFORE the item's anchor (task `due_at` else
 * `starts_at`; event `starts_at`). Pinned by
 * common-contracts/relations_reminder_offsets.json — see
 * relations-service/app/models/reminder_offsets.rb for the Ruby half.
 */
export type ReminderOffsetMinutes = 0 | 15 | 60 | 1440 | 10080;
export const REMINDER_OFFSET_MINUTES: readonly ReminderOffsetMinutes[] = [0, 15, 60, 1440, 10080];
export const REMINDER_MAX_PER_ITEM = 3;

/** One scheduled reminder on a task or event, as relationsvc serializes it. */
export interface Reminder {
  id: Uuid;
  offset_minutes: ReminderOffsetMinutes;
  /** UTC instant: the item's anchor minus `offset_minutes`. */
  fire_at: string;
  /** Non-null once it has been delivered; a fired reminder never fires again. */
  fired_at: string | null;
}

export interface Task {
  id: Uuid;
  task_type_id: Uuid;
  title: string;
  notes: string | null;
  status: TaskStatus;
  display_status: TaskDisplayStatus;
  /** UTC instants; all-day tasks store shop-local 00:00. */
  starts_at: string | null;
  due_at: string | null;
  all_day: boolean;
  assignee_id: Uuid | null;
  partner_id: Uuid | null;
  partner_name: string | null;
  contact_id: Uuid | null;
  contact_name: string | null;
  discussion_id: Uuid | null;
  created_by_id: Uuid | null;
  reminders: Reminder[];
  recurrence_id: Uuid | null;
  occurrence_index: number | null;
  detached: boolean;
  recurrence_summary: RecurrenceSummary | null;
  created_at: string;
  updated_at: string;
}

export interface TaskInput {
  task_type_id: Uuid;
  title: string;
  notes: string;
  status: TaskStatus;
  starts_at: string | null;
  due_at: string | null;
  all_day: boolean;
  assignee_id: Uuid | null;
  /** Omitting the key leaves the server's rows untouched; sending [] clears them. */
  reminder_offsets?: ReminderOffsetMinutes[];
  partner_id: Uuid | null;
  contact_id: Uuid | null;
  discussion_id: Uuid | null;
}

export interface RelationsEvent {
  id: Uuid;
  event_type_id: Uuid;
  title: string;
  notes: string | null;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  partner_id: Uuid | null;
  partner_name: string | null;
  contact_id: Uuid | null;
  contact_name: string | null;
  discussion_id: Uuid | null;
  created_by_id: Uuid | null;
  attendee_ids: Uuid[];
  reminders: Reminder[];
  recurrence_id: Uuid | null;
  occurrence_index: number | null;
  detached: boolean;
  recurrence_summary: RecurrenceSummary | null;
  created_at: string;
  updated_at: string;
}

export interface RelationsEventInput {
  event_type_id: Uuid;
  title: string;
  notes: string;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  partner_id: Uuid | null;
  contact_id: Uuid | null;
  discussion_id: Uuid | null;
  attendee_ids: Uuid[];
  reminder_offsets?: ReminderOffsetMinutes[];
}

/** GET /calendar — tasks with a start or due instant, and events with a span, inside [from, to). */
export interface CalendarData {
  tasks: Task[];
  events: RelationsEvent[];
}

/** GET /users/staff row — staff of the account, readable by sales staff and admins (a Relations grant). */
export interface StaffUser {
  id: Uuid;
  name: string;
  email: string;
  roles: string[];
}

/** GET /users/lookup row — identity only, never roles or status. */
export interface UserLookupRow {
  id: Uuid;
  name: string;
  email: string;
}

/** `ids` (comma-separated) wins over `q` server-side. */
export interface UserLookupParams {
  q?: string;
  exact?: boolean;
  ids?: string;
}

/**
 * One line item in an admin cart, per `AdminCartsController#serialize_line_item`.
 */
export interface AdminCartLineItem {
  id: Uuid;
  product_id: Uuid;
  album_id: Uuid;
  /**
   * Display name looked up from product-service (admin product payload, so a
   * nested `TranslatedString`). Null when the lookup fails. Localize with
   * `pickLocalized` before rendering.
   */
  product_name: TranslatedString | null;
  quantity: number;
  unit_price: string;
  /** Line total (quantity × unit_price), serialized as `price` on the backend. */
  price: string;
  currency_code: string | null;
}

/**
 * Admin cart returned by `GET /direct/ordersvc/admin/carts?user_id=:id`.
 * Mirrors `AdminCartsController#serialize_admin_cart`.
 * The cart-level total field is `items_total` (not `total_amount`).
 */
export interface AdminCart {
  id: Uuid;
  user_id: Uuid;
  account_id: Uuid;
  line_items: AdminCartLineItem[];
  /** Sum of all line item prices. */
  items_total: string;
  currency_code: string | null;
}

export interface AlbumProductAssignment {
  product_id: Uuid;
  price: string;
}

export interface AlbumProductsResponse {
  assignments: AlbumProductAssignment[];
}

export interface EffectivePriceEntry {
  product_id: Uuid;
  price: string;
  source_album_id: Uuid;
}

export interface EffectiveAlbumProductsResponse {
  effective: EffectivePriceEntry[];
}

export interface AlbumIdsWithPricesResponse {
  album_ids: Uuid[];
  /** Present only when the request passed `ids`: local price rows per album. */
  counts?: Record<Uuid, number>;
}

// === Checkout admin: shared types ===

// Backend's TranslatableAttributes.serialize returns nil for locales with
// no stored value (it uses fallback: false). Locales not in the account's
// enabled set are also omitted/null at the API boundary. Keep this aligned
// with reality so consumers can lean on TypeScript rather than runtime nil-
// checks.
export type TranslatedString = Partial<Record<Locale, string | null>>;

export type ParamLeaf = string | number | boolean | null;

// Recursive: a template node is either a leaf (typed example) or a nested object.
// `secret_fields` is a reserved key: when present at the top level it declares
// which leaf key names are secrets (masked at rest, returned as "__set__" by the
// admin API). It is NOT itself a form leaf — consumers must strip it before
// iterating template entries.
export interface ParamsTemplate {
  secret_fields?: string[];
  [key: string]: ParamLeaf | ParamsTemplate | string[] | undefined;
}

// Submitted values mirror the template shape; leaves may also be null/missing.
export interface ParamValues {
  [key: string]: ParamLeaf | ParamValues | undefined;
}

export interface OptionConstraints {
  subtree?: Uuid[];
  country?: string[];
}

export type GsoTechid = 'custom' | 'post';
export type GpoTechid = 'custom' | 'iban' | 'cash' | 'paypal' | 'barion' | 'stripe' | 'simplepay' | 'gift';

export interface GlobalShippingOption {
  id: Uuid;
  techid: GsoTechid;
  name: string;
  descr: string;
  default_processing_days: number;
  needs_address: boolean;
  params: ParamsTemplate;
}

export interface GlobalPaymentOption {
  id: Uuid;
  techid: GpoTechid;
  name: string;
  descr: string;
  default_processing_days: number;
  manual_completion: boolean;
  params: ParamsTemplate;
}

export type OptionFieldRefAttribute = 'valid_from' | 'valid_until' | 'fix_delivery_date';
export type OptionFieldRefs = Partial<Record<OptionFieldRefAttribute, string>>;

/** POST /albums/resolve_refs (picsvc Albums::FieldRefResolver). Unresolved refs are absent. */
export interface FieldRefResolution {
  paths: Record<Uuid, Uuid[]>;
  values: Record<Uuid, Record<string, { value: AlbumCustomValue; kind: AlbumTypeFieldKind; source_album_id: Uuid }>>;
}

interface OptionBase {
  id: Uuid;
  name: TranslatedString;
  descr: TranslatedString;
  index: number;
  disabled: boolean;
  processing_days: number;
  valid_from: string | null;
  valid_until: string | null;
  param_values: ParamValues;
  constraints: OptionConstraints;
  image_url: string | null;
  /** Attributes driven by an album field reference instead of a static date (§3, R8: never both). */
  field_refs: OptionFieldRefs;
}

export interface ShippingOptionForAdmin extends OptionBase {
  global_shipping_option: GlobalShippingOption;
  fix_delivery_date: string | null;
  needs_address: boolean;
  /** Short code printed in the produced-orders envelope's corner; null prints nothing. */
  envelope_marker: string | null;
  price_by_category: Record<string, number>;
}

export interface PaymentOptionForAdmin extends OptionBase {
  global_payment_option: GlobalPaymentOption;
  price: number;
  info_details: string;
  /** Server-computed invoicing status: whether enabling this option is currently blocked. */
  invoicing?: { enable_blocked: boolean };
}

export type PrintJobStatus = 'queued' | 'running' | 'ready' | 'failed' | 'expired';

export type PrintInputKind =
  | 'album_subtree_root'
  | 'album_types'
  | 'production_group'
  | 'date'
  | 'date_range'
  | 'enum'
  | 'text'
  | 'boolean'
  | 'rich_text';

export interface PrintInputField {
  name: string;
  kind: PrintInputKind;
  optional?: boolean;
  multiline?: boolean;
  default?: unknown;
  values?: string[];
  label_key?: string;
  templatable?: boolean;
  merge_catalog?: MergeCatalog;
  qr?: boolean;
  rule?: boolean;
  help_key?: string;
}

export interface MergeCatalogField {
  key: string;
  label_key: string;
}

export interface MergeCatalogNamespace {
  prefix: string;
  label_key: string;
  /** Set on the `album.field` / `album.inherited` namespaces (album-field-refs §3). */
  key_source?: 'album_fields';
}

export interface MergeCatalog {
  fields: MergeCatalogField[];
  namespaces: MergeCatalogNamespace[];
}

export interface RichTextRun {
  text?: string;
  ref?: string;
  fallback?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  font?: string;
  size?: number;
  color?: string;
}

export type RichTextAlign = 'left' | 'center' | 'right' | 'justify';
export type QrSize = 's' | 'm' | 'l';

export interface RichTextTextBlock {
  align?: RichTextAlign;
  runs: RichTextRun[];
}

export interface RichTextQrBlock {
  // QR blocks support horizontal placement only (no justify).
  align?: 'left' | 'center' | 'right';
  qr: { ref: string; size: QrSize };
}

// A full-width horizontal separator line; carries no content or styling.
export interface RichTextRuleBlock {
  rule: true;
}

export type RichTextBlock = RichTextTextBlock | RichTextQrBlock | RichTextRuleBlock;

export interface RichTextDoc {
  v: 1;
  blocks: RichTextBlock[];
}

export const EMPTY_RICH_TEXT_DOC: RichTextDoc = { v: 1, blocks: [] };

export interface AutoPrintEventRef {
  key: string;
  label_key: string;
}

export interface PrintType {
  id: string;
  label_key: string;
  contexts: string[];
  inputs: { fields: PrintInputField[] };
  prefill: Record<string, unknown>;
  auto_print_events: AutoPrintEventRef[];
}

export interface PrintJob {
  id: Uuid;
  account_id: Uuid;
  /** Null for an auto print (made by an event, not a person). */
  user_id: Uuid | null;
  name: string;
  print_type_id: string;
  inputs: Record<string, unknown>;
  trigger_event: string | null;
  subject_id: Uuid | null;
  print_template_id: Uuid | null;
  status: PrintJobStatus;
  item_count: number | null;
  /** True when this job's albums count as printed for its template (print history). */
  records_subjects: boolean;
  error_message: string | null;
  expires_at: string | null;
  created_at: string;
  pdf_url?: string;
  /** Inline-disposition presigned URL for opening the PDF in a browser tab. */
  pdf_view_url?: string;
  pdf_url_expires_at?: string;
}

/** GET /print_jobs?trigger_event=… — auto jobs of a page of subjects. */
export interface AutoPrintJobs {
  jobs: PrintJob[];
  /** Print types with a template bound to the event. */
  bound_types: string[];
}

export interface CreatePrintJobPayload {
  print_type_id: string;
  name: string;
  inputs: Record<string, unknown>;
  print_template_id?: Uuid;
}

export interface PrintTemplate {
  id: Uuid;
  print_type_id: string;
  name: string;
  values: Record<string, unknown>;
  auto_print_event: string | null;
  created_at: string;
  updated_at: string;
}

export interface PrintTemplatePayload {
  print_type_id: string;
  name: string;
  values: Record<string, unknown>;
  auto_print_event?: string | null;
}

// ---------------------------------------------------------------------------
// Invoicing types (Task 26)
// ---------------------------------------------------------------------------

export type InvoiceStatus = 'pending' | 'issued' | 'failed' | 'stornoed';

/** The invoices page tabs (GET /admin/invoices?tab=). */
export type InvoiceTab = 'todo' | 'issued' | 'stornoed' | 'all';

/** Invoice::ErrorExplainer codes; the UI translates admin.invoices.error.<code>. */
export type InvoiceErrorCode =
  | 'invalid_buyer_email' | 'config' | 'provider_unreachable' | 'provider' | 'previous_provider' | 'already_cancelled'
  | 'storno_failed';

export type InvoicePdfKind = 'invoice' | 'storno';

/** GET /admin/invoices/counts — account-wide. Teendő = error + warning. */
export interface InvoiceCounts {
  error: number;
  warning: number;
  issued: number;
  stornoed: number;
  all: number;
}

export interface AdminInvoice {
  id: Uuid;
  /** The order's billing address name (the name on the invoice). */
  buyer_name?: string | null;
  order_payment_id: Uuid;
  status: InvoiceStatus;
  manual: boolean;
  /** Techid of the invoicing provider that issued (or is issuing) this invoice. */
  issuing_provider?: string;
  /** Display name of the issuing provider (falls back to the techid server-side). */
  issuing_provider_name?: string;
  /** The issuing provider issues documents itself (not the manual one): an attempt through it may have left a document there. */
  issuing_provider_automated?: boolean;
  /** The shop has switched providers since: this document lives in the old provider's account, so storno is recorded by hand. */
  from_previous_provider?: boolean;
  invoice_number: string | null;
  last_error: string | null;
  /** Server-side classification of last_error; null when there is none. */
  error_code?: InvoiceErrorCode | null;
  /** POST /admin/invoices/:id/document_link would mint a link for kind=invoice / kind=storno. */
  pdf_available?: boolean;
  storno_pdf_available?: boolean;
  issued_at: string | null;
  /** Set while a worker holds the issuance claim; a claim older than 10 min is stale (worker died) and Retry clears it. */
  issuing_at?: string | null;
  /** Set while a storno call is (or was, uncertainly) in flight; older than 10 min it is stale and the admin may record the storno by hand. */
  storno_issuing_at?: string | null;
  storno_invoice_number?: string | null;
  storno_at?: string | null;
  order_number?: string;
  payment_method?: string;
  /** The payment option's name in the request locale; show this, fall back to the techid. */
  payment_method_name?: string | null;
  amount?: string | number | null;
  payment_completed_at?: string;
  currency_code?: string;
  /** Order this invoice belongs to (one live invoice per order). */
  order_id?: Uuid;
  /** Order total at issue time. */
  invoiced_amount?: string | number | null;
  /** Current order total (server-computed). */
  order_total?: string | number | null;
  /** order_total - invoiced_amount; non-zero = stale invoice, admin amends by hand. 0/absent = fine. */
  invoice_mismatch?: string | number | null;
  amended_note?: string | null;
  amended_at?: string | null;
}

export type ResolveImbalanceResolution = 'accept_underpayment' | 'refund_excess' | 'keep_excess';

export interface ResolveImbalanceBody {
  resolution: ResolveImbalanceResolution;
  /** refund_excess only: manual-completion payment option used for the refund. */
  payment_option_id?: Uuid;
  note?: string;
  /** ISO date (YYYY-MM-DD) the refund was disbursed; refund_excess only. */
  settled_on?: string;
}

export interface GlobalInvoiceProviderDTO {
  id: Uuid;
  techid: string;
  name: string;
  descr: string;
  automated: boolean;
  params: Record<string, unknown>;
}

export interface AdminInvoiceProvider {
  id: Uuid;
  global_invoice_provider: GlobalInvoiceProviderDTO;
  param_values: Record<string, unknown>;
  payment_option_overrides: Record<string, unknown>;
  disabled: boolean;
}

export interface InvoiceListParams {
  tab?: InvoiceTab;
  per_page?: number;
  handling?: string;
  from?: string;
  to?: string;
  q?: string;
  page?: number;
  /** Plumbed through for future use; the admin invoices table has no sortable headers. */
  sort?: string;
  order_id?: string;
}

export interface SaveProviderBody {
  global_invoice_provider_id: Uuid;
  param_values?: Record<string, unknown>;
  payment_option_overrides?: Record<string, unknown>;
  disabled?: boolean;
}

export interface LabeledOption {
  value: string;
  label: string;
}

export interface ConnectionTestResult {
  status: 'ok' | 'invalid_credentials' | 'no_access' | 'error' | 'unconfigured';
  ok_to_operate?: boolean;
  plan_label?: string | null;
  valid_until?: string | null;
  /** Labeled options the provider supplied for per-option fields, by field key. */
  dynamic_field_options?: Record<string, LabeledOption[]>;
}

export interface TestConnectionBody {
  global_invoice_provider_id: Uuid;
  param_values?: Record<string, unknown>;
}

// --- Analytics (shop analytics slice 2) ---
export type AnalyticsGranularity = 'day' | 'week' | 'month';
export interface AnalyticsPoint { bucket: string; value: number }
export interface AnalyticsEnvelope {
  metric: string;
  granularity: AnalyticsGranularity | 'none';
  scope: { album_root: string | null };
  series?: AnalyticsPoint[];
  total: number;
}
export type AnalyticsService = 'ordersvc' | 'picsvc' | 'usersvc';
export type GuestFunnelOutcome = 'registered' | 'registered_unverified' | 'merged' | 'dropped_off';

// --- Bank transfer reconciliation ---

export type BankReconOutcome =
  | 'exact_match'
  | 'multi_exact'
  | 'amount_mismatch'
  | 'ambiguous'
  | 'unmatched'
  | 'already_settled'
  | 'already_imported'
  | 'ignored_outgoing';

export interface BankReconOrderRef {
  id: Uuid;
  order_number: string;
  outstanding: string;
  currency_code: string;
  /**
   * Acceptance warnings this order carries on the transaction's settlement
   * date (spec §6.4). Present on matched orders and suggestions alike; empty
   * when the payment may be registered without a second thought.
   */
  warnings: PaymentAcceptanceWarning[];
}

export interface BankReconRow {
  fingerprint: string;
  booking_date: string | null;
  value_date: string | null;
  /** value_date ?? booking_date — the date that will land on the invoice. */
  settlement_date: string | null;
  amount: string;
  currency: string;
  remittance: string | null;
  counterparty_name: string | null;
  outcome: BankReconOutcome;
  proposed_action: 'register' | 'skip';
  matched_orders: BankReconOrderRef[];
  suggestions: BankReconOrderRef[];
  raw: (string | null)[];
}

export interface BankReconPlan {
  detected: {
    delimiter: string;
    currency: string;
    mapping: Record<string, number>;
    header: string[] | null;
  };
  counts: { ready: number; attention: number; already_imported: number; ignored: number };
  rows: BankReconRow[];
}

export interface BankReconDecision {
  fingerprint: string;
  action: 'register' | 'assign' | 'skip';
  order_id?: Uuid;
  /**
   * The warning codes the admin explicitly accepted for this row's target
   * order(s). order-service refuses a register/assign whose target carries a
   * warning this list does not cover, counting it as
   * `summary_counts.skipped_needs_confirmation`.
   */
  accept_warnings?: string[];
}

export interface BankReconPreviewPayload {
  csv: string;
  fallback_currency?: string;
  mapping?: Record<string, number>;
}

export interface BankReconApplyPayload extends BankReconPreviewPayload {
  filename: string;
  decisions: BankReconDecision[];
}

export interface BankReconApplyResult {
  id: Uuid;
  summary_counts: Record<string, number>;
}

// --- Analytics user count (slice 4 / C3) ---
export interface UserCountQuery {
  from: string;
  to: string;
  granularity: AnalyticsGranularity | 'none';
  ordered: boolean;
  paid: boolean;
  associated: boolean;
  loginWithin?: 7 | 30;
}

// --- Analytics list reports (slice 3) ---
export type PaidFilter = 'all' | 'paid' | 'unpaid';
/**
 * subtree_users audience filter. "guest" means a checkout guest (user-service's
 * `guest` flag), "registered" everyone else. picsvc can't tell them apart on its
 * own — it hydrates the flag from user-service — so this is a server-side param,
 * never a client-side filter over the current page.
 */
export type UserKindFilter = 'all' | 'registered' | 'guest';
export interface ReportColumn { key: string; header: string }
export type ReportRow = Record<string, string | number | null>;
export interface ReportEnvelope {
  report: string;
  columns: ReportColumn[];
  scope: { album_root: string | null };
  page: number;
  per_page: number;
  total_count: number;
  rows: ReportRow[];
}

// --- Promotions (shop promotions feature) ---
export type PromotionKind = 'additive' | 'multiplicative';
export type PromotionStatus = 'upcoming' | 'active' | 'expired';

/** Shared discount shape driving effectiveUnitPrice (see promotionPrice.ts). */
export interface PromotionDiscount {
  kind: PromotionKind;
  amount?: number | string | null;
  percent?: number | string | null;
}

/** The promotion actually applied to a cart line item, as embedded by order-service. */
export interface CartItemPromotion extends PromotionDiscount {
  id: Uuid;
  name: string;
}

/** Enduser-facing projection from GET /direct/ordersvc/promotions/active. */
export interface ActivePromotion extends PromotionDiscount {
  id: Uuid;
  name: string;
  product_ids: Uuid[];
}

/** Admin CRUD projection from /direct/ordersvc/promotions. */
export interface AdminPromotion extends ActivePromotion {
  album_root_id: Uuid | null;
  starts_at: string;
  ends_at: string;
  status: PromotionStatus;
  created_at?: string;
}

export interface AdminPromotionPayload {
  name: string;
  kind: PromotionKind;
  amount?: number | null;
  percent?: number | null;
  album_root_id: Uuid | null;
  product_ids: Uuid[];
  starts_at: string;
  ends_at: string;
}

// --- Album notifications (album-notifications spec §4) ---

/** GET /direct/picsvc/albums/:id/subscription_status (album-notifications spec §4.2). */
export interface AlbumSubscriptionStatus {
  visitor_signup_enabled: boolean;
  /** An active linked subscription exists for the signed-in user (always false for guests). */
  subscribed: boolean;
}

export type AlbumSubscriptionSource = 'visitor' | 'self_verified' | 'assoc' | 'admin' | 'foto2sort';

/** One row of GET /direct/picsvc/admin/album_subscriptions (spec §4.4). */
export interface AdminAlbumSubscription {
  id: Uuid;
  /** Null for a linked row: delivered to the owner's account address (`user_email`). */
  email: string | null;
  user_id: Uuid | null;
  user_email: string | null;
  user_name: string | null;
  /** True when the owner is a guest session (user_email/user_name are then null). */
  owner_guest: boolean;
  owner: 'shop' | 'user';
  source: AlbumSubscriptionSource;
  consented_at: string;
  unsubscribed_at: string | null;
  created_at: string;
}

export type AlbumSubscriptionSkipReason = 'invalid' | 'exists' | 'unsubscribed' | 'cap';

export interface AdminAddSubscriptionsResult {
  added: string[];
  skipped: { email: string; reason: AlbumSubscriptionSkipReason }[];
}
